import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { usePermission } from '../../../hooks/usePermission';
import { useUsers } from '../../../hooks/useUsers';
import { useUsersTableState } from '../../../hooks/useUsersTableState';
import { useAllTeams } from '../../../graphql/queries';
import { DeleteConfirmationDialog } from '../../../components/shared/DeleteConfirmationDialog';
import { Pagination } from '../../../components/shared/Pagination';
import {
  RoleBadge,
  SourceBadge,
  TeamChips,
  AssignTeamDialog,
  AddUserDialog,
  EditRoleDialog,
} from '../../../components/users';
import { ALL_ROLES, getRoleLabel } from '../../../types/users';
import type { PlatformUser, UserRole } from '../../../types/users';
import { buildUserTeamsMap, formatLastLogin, type UserSortColumn } from '../../../lib/usersFilter';
import '@tylertech/forge/button';
import '@tylertech/forge/banner';
import '@tylertech/forge/dialog';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/admin/users/')({
  component: UsersAdminPage,
});

export { UsersAdminPage };

const BannerElement = 'forge-banner' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;

function UsersAdminPage(): JSX.Element {
  const canManageUsers = usePermission('user:manage');
  const canCreateUsers = usePermission('user:create');
  const canDeleteUsers = usePermission('user:delete');
  const apolloClient = useApolloClient();
  const { users, loading, error, createUser, updateRoles, deleteUser, refetch } = useUsers();
  // `useAllTeams` auto-paginates past a single server page (issue #1354) —
  // a hardcoded `useTeams(undefined, 200)` isn't truncating today (prod
  // Team count ~41) but has no way to catch up as the catalog grows past
  // 200; feeds the team-filter dropdown (`teamFilterOptions` below).
  const { data: teamsData } = useAllTeams();

  const allTeams = useMemo(() => teamsData?.teams?.edges?.map((e) => e.node) ?? [], [teamsData]);
  const userTeamsMap = useMemo(() => buildUserTeamsMap(allTeams), [allTeams]);

  const {
    search,
    roleFilter,
    teamFilter,
    sortCol,
    sortDir,
    page,
    pageSize,
    pagedUsers,
    totalFilteredCount,
    teamFilterOptions,
    handleSearchChange,
    handleRoleFilterChange,
    handleTeamFilterChange,
    handleSort,
    handlePageSizeChange,
    setPage,
  } = useUsersTableState(users, allTeams, userTeamsMap);

  const [addOpen, setAddOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const [editTarget, setEditTarget] = useState<PlatformUser | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<PlatformUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [assignTarget, setAssignTarget] = useState<PlatformUser | null>(null);

  const sortableHeader = (col: UserSortColumn, label: string): JSX.Element => {
    const active = sortCol === col;
    return (
      <th
        className="users-th-sortable"
        onClick={() => handleSort(col)}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleSort(col);
        }}
        data-testid={`sort-${col}`}
      >
        {label}
        {active && (
          <span aria-hidden="true" className="users-sort-icon">
            {sortDir === 'asc' ? ' ▲' : ' ▼'}
          </span>
        )}
      </th>
    );
  };

  const handleTeamMutate = async (): Promise<void> => {
    await apolloClient.refetchQueries({ include: ['ListTeams', 'GetTeam'] });
  };

  if (!canManageUsers) {
    return (
      <div className="users-page">
        <h1 className="forge-typography--headline5">User Management</h1>
        <BannerElement theme="error" dismissed={false} role="alert">
          <span>Access Restricted — Administrator or RoleAdministrator role required.</span>
        </BannerElement>
      </div>
    );
  }

  const handleAdd = async (data: {
    email: string;
    name: string;
    roles: UserRole[];
  }): Promise<void> => {
    setIsAdding(true);
    try {
      await createUser(data);
      setAddOpen(false);
    } finally {
      setIsAdding(false);
    }
  };

  const handleEditSubmit = async (roles: UserRole[]): Promise<void> => {
    if (!editTarget) return;
    setIsEditing(true);
    try {
      await updateRoles(editTarget.userId, { roles });
      setEditTarget(null);
    } finally {
      setIsEditing(false);
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteUser(deleteTarget.userId);
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="users-page">
      <div className="users-page-header">
        <h1 className="forge-typography--headline5">User Management</h1>
        {canCreateUsers && (
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => setAddOpen(true)}
            data-testid="add-user-button"
          >
            Add User
          </ForgeButton>
        )}
      </div>

      <p className="forge-typography--body1 users-subtitle">
        Manage platform user access. Users without an existing TID-W account will be
        auto-provisioned on first login.
      </p>

      {error && (
        <BannerElement theme="error" dismissed={false} role="alert" className="users-error-banner">
          <span>{error}</span>
          <ForgeButton
            slot="action"
            type="button"
            variant="outlined"
            onClick={() => void refetch()}
          >
            Retry
          </ForgeButton>
        </BannerElement>
      )}

      <div className="users-filters-row">
        <input
          type="text"
          className="search-input users-filter-search"
          placeholder="Search by email or name…"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          aria-label="Search users by email or name"
          data-testid="users-search-input"
        />
        <select
          className="filter-select"
          value={roleFilter}
          onChange={(e) => handleRoleFilterChange(e.target.value as UserRole | '')}
          aria-label="Filter by role"
          data-testid="users-role-filter"
        >
          <option value="">All Roles</option>
          {ALL_ROLES.map((role) => (
            <option key={role} value={role}>
              {getRoleLabel(role)}
            </option>
          ))}
        </select>
        <select
          className="filter-select"
          value={teamFilter}
          onChange={(e) => handleTeamFilterChange(e.target.value)}
          aria-label="Filter by team"
          data-testid="users-team-filter"
        >
          <option value="">All Teams</option>
          {teamFilterOptions.map((t) => (
            <option key={t.entityId} value={t.entityId}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="users-table-skeleton" aria-busy="true" aria-label="Loading users" />
      ) : (
        <div className="users-table-wrapper">
          <table className="users-table" aria-label="Platform users">
            <thead>
              <tr>
                {sortableHeader('email', 'Email')}
                {sortableHeader('name', 'Name')}
                <th>Roles</th>
                <th>Teams</th>
                {sortableHeader('source', 'Source')}
                {sortableHeader('lastLoggedInAt', 'Last Login')}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="users-table-empty">
                    No users found.
                  </td>
                </tr>
              ) : totalFilteredCount === 0 ? (
                <tr>
                  <td colSpan={7} className="users-table-empty">
                    No users match the current filters.
                  </td>
                </tr>
              ) : (
                pagedUsers.map((user) => {
                  const userTeams = userTeamsMap.get(user.userId) ?? [];
                  return (
                    <tr key={user.userId}>
                      <td>{user.email}</td>
                      <td>{user.name}</td>
                      <td>
                        {user.roles.map((r) => (
                          <RoleBadge key={r} role={r} />
                        ))}
                      </td>
                      <td className="users-cell-teams">
                        <TeamChips teams={userTeams} />
                      </td>
                      <td>
                        <SourceBadge source={user.source ?? 'manual'} />
                      </td>
                      <td
                        className="users-cell-last-login"
                        title={user.lastLoggedInAt ?? 'Never logged in'}
                        data-testid={`last-login-${user.userId}`}
                      >
                        {formatLastLogin(user.lastLoggedInAt)}
                      </td>
                      <td className="users-cell-actions">
                        <ForgeButton
                          type="button"
                          variant="outlined"
                          className="users-action-btn"
                          onClick={() => setEditTarget(user)}
                          aria-label={`Edit roles for ${user.email}`}
                          data-testid={`edit-role-${user.userId}`}
                        >
                          Edit Roles
                        </ForgeButton>
                        <ForgeButton
                          type="button"
                          variant="outlined"
                          className="users-action-btn"
                          onClick={() => setAssignTarget(user)}
                          aria-label={`Assign teams for ${user.email}`}
                          data-testid={`assign-teams-${user.userId}`}
                        >
                          Assign Teams
                        </ForgeButton>
                        {canDeleteUsers && (
                          <ForgeButton
                            type="button"
                            variant="outlined"
                            className="users-action-btn users-action-btn--danger"
                            onClick={() => setDeleteTarget(user)}
                            aria-label={`Remove ${user.email}`}
                            data-testid={`remove-user-${user.userId}`}
                          >
                            Remove
                          </ForgeButton>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <Pagination
            totalCount={totalFilteredCount}
            pageSize={pageSize}
            currentPage={page}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      <AddUserDialog
        open={addOpen}
        isSubmitting={isAdding}
        onSubmit={(data) => void handleAdd(data)}
        onCancel={() => setAddOpen(false)}
      />

      <EditRoleDialog
        open={editTarget !== null}
        user={editTarget}
        isSubmitting={isEditing}
        onSubmit={(roles) => void handleEditSubmit(roles)}
        onCancel={() => setEditTarget(null)}
      />

      <AssignTeamDialog
        open={assignTarget !== null}
        user={assignTarget}
        allTeams={allTeams}
        userTeams={assignTarget ? (userTeamsMap.get(assignTarget.userId) ?? []) : []}
        onClose={() => setAssignTarget(null)}
        onMutate={handleTeamMutate}
      />

      <DeleteConfirmationDialog
        open={deleteTarget !== null}
        title="Remove User"
        message="This will revoke platform access. The user will receive Read-Only access on their next login if re-added."
        itemName={deleteTarget?.email ?? ''}
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={() => setDeleteTarget(null)}
        isDeleting={isDeleting}
      />
    </div>
  );
}
